import React from 'react';
import { UserPlus, Store, Users, BarChart3, Brain } from 'lucide-react';
import AnimatedGradientText from './AnimatedGradientText';
import AtlasSahne from './urun/AtlasSahne';
import { useLang } from './LanguageProvider';
import translations from '../translations';

/* İKONLAR ADIM METİNLERİYLE EŞLEŞİR.
   Adımlar eski "danışmanlık başvurusu" akışından gerçek akışa çevrildi:
   Kaydol → Mağaza Ekle → Arkadaşına Yetki Ver → Sipariş ve Kârı İzle.
   İkonlar da o sırayla yenilendi; eski dizi (FileText=başvuru formu,
   MessageSquare=müşteri etkileşimi) artık var olmayan adımları anlatıyordu.
   Dizi uzunluğu 4'te sabit — `steps` dizisi `[t.s1..t.s4]` ile kurulu ve
   `colors` da 4 elemanlı; sayı değişirse ikon `undefined` olur. */
const icons = [UserPlus, Store, Users, BarChart3];
const colors = ['#06b6d4', '#3b82f6', '#10b981', '#8b5cf6'];

export default function HowItWorks() {
  const { lang } = useLang();
  const t = translations[lang].howItWorks;
  const steps = [t.s1, t.s2, t.s3, t.s4];
  const aiStep = t.ai;

  return (
    <section data-testid="how-it-works-section" id="how-it-works" className="py-20 sm:py-24 lg:py-28 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0" style={{ background: 'radial-gradient(900px, rgba(6, 182, 212, 0.06), transparent 55%)' }} />
      </div>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full dark:bg-cyan-500/10 bg-cyan-50 border dark:border-cyan-500/20 border-cyan-200 mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 animate-pulse" />
            <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-cyan-500">{t.badge}</span>
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4"><AnimatedGradientText>{t.title}</AnimatedGradientText></h2>
          <p className="text-base dark:text-white/50 text-slate-500 max-w-lg mx-auto">{t.desc}</p>
        </div>
        <div className="relative">
          <div className="hidden lg:block absolute left-[12%] right-[12%] h-px" style={{ top: '72px' }}>
            <div className="h-full bg-gradient-to-r from-cyan-500/25 via-blue-500/25 via-emerald-500/25 to-purple-500/25" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-5">
            {steps.map((step, i) => {
              const Icon = icons[i];
              const color = colors[i];
              return (
                <div key={i} data-testid={`how-step-${i}`} className="text-center relative">
                  <div className="relative inline-flex mb-7">
                    <div className="w-16 h-16 rounded-2xl flex items-center justify-center relative z-10 transition-transform hover:scale-105" style={{ background: `${color}1f`, border: `1px solid ${color}40` }}>
                      <Icon className="w-7 h-7" style={{ color }} />
                    </div>
                    <div className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white z-20 shadow-lg" style={{ background: color }}>{i + 1}</div>
                    <div className="absolute inset-0 rounded-2xl blur-xl opacity-20" style={{ background: `${color}25` }} />
                  </div>
                  <h3 className="text-base font-bold mb-2 dark:text-white text-slate-900">{step.title}</h3>
                  <p className="text-sm dark:text-white/50 text-slate-500 leading-relaxed max-w-[200px] mx-auto">{step.desc}</p>
                </div>
              );
            })}
          </div>
        </div>

        {aiStep && (
          <div className="mt-10 sm:mt-14 relative">
            <div className="hidden lg:flex justify-between px-[12%] mb-0" style={{ position: 'relative' }}>
              {colors.map((color, i) => (
                <div key={i} className="flex flex-col items-center" style={{ flex: 1 }}>
                  <div className="w-px h-8" style={{ background: `linear-gradient(to bottom, ${color}60, #a855f780)` }} />
                </div>
              ))}
            </div>
            <div className="hidden lg:block mx-auto mb-0" style={{ width: '76%' }}>
              <div className="h-px" style={{ background: 'linear-gradient(to right, #06b6d440, #3b82f640, #10b98140, #8b5cf640)' }} />
            </div>
            <div className="flex justify-center">
              <div className="w-px h-8 sm:h-10 lg:h-8" style={{ background: 'linear-gradient(to bottom, #a855f780, #ec489980)' }} />
            </div>
            <div className="flex justify-center mb-0">
              <div className="w-3 h-3 rounded-full" style={{ background: 'linear-gradient(135deg, #a855f7, #ec4899)', boxShadow: '0 0 12px rgba(168,85,247,0.4)' }} />
            </div>
            <div className="flex justify-center">
              <div className="w-px h-4" style={{ background: 'linear-gradient(to bottom, #ec489980, #a855f740)' }} />
            </div>
            <div className="max-w-md mx-auto text-center">
              <div className="relative inline-flex mb-5">
                {/* ATLAS 3B ROBOTU — crm.whowsell.com'daki sahnenin aynısı.
                    Kutu artık 64px değil, 132-176px arası kare (gerekçesi
                    rakamlarıyla App.css · .wh-atlas-kutu'da). Kare oran,
                    mor/pembe gradyan zemin, kenarlık, köşe yarıçapı ve
                    AI rozeti korundu.
                    Sahne yüklenemezse / WebGL yoksa / kullanıcı hareket
                    istemiyorsa kutuda eskisi gibi Brain ikonu duruyor —
                    kutu hiçbir karede boş kalmıyor. */}
                <div className="wh-atlas-kutu rounded-2xl flex items-center justify-center relative z-10 transition-transform hover:scale-105" style={{ background: 'linear-gradient(135deg, rgba(168,85,247,0.2), rgba(236,72,153,0.15))', border: '1px solid rgba(168,85,247,0.4)' }}>
                  <AtlasSahne yedek={<Brain className="w-7 h-7" style={{ color: '#a855f7' }} />} />
                </div>
                <div className="absolute -top-1.5 -right-1.5 px-2 h-6 rounded-full flex items-center justify-center text-[9px] font-bold text-white z-20 shadow-lg" style={{ background: 'linear-gradient(135deg, #a855f7, #ec4899)' }}>AI</div>
                <div className="absolute inset-0 rounded-2xl blur-xl opacity-25" style={{ background: 'linear-gradient(135deg, #a855f7, #ec4899)' }} />
              </div>
              <h3 className="text-base font-bold mb-2 dark:text-white text-slate-900">{aiStep.title}</h3>
              <p className="text-sm dark:text-white/50 text-slate-500 leading-relaxed max-w-[280px] mx-auto">{aiStep.desc}</p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
